import React, { useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  Container,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  Grid,
  Text,
  GridItem,
  Heading,
  Progress,
  Center,
  Skeleton,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from "@chakra-ui/react";
import AdminDataTable from "components/AdminDataTable/AdminDataTable";
import { AiOutlineSearch } from "react-icons/ai";
import { userAnalyticRequest, subscriptionPlanRequest } from "services/request";
import { QUERY_KEYS } from "utils/constants";
import { useQueryWrapper } from "services/apiHelper";
import dayjs from "dayjs";
import Paginator from "components/Paginator";
import { useQuery } from "react-query";
import axiosInstance from "services/httpService";
import { GrLineChart } from "react-icons/gr";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { BiFilter } from "react-icons/bi";
import { FaSortAlphaUpAlt, FaSortAlphaDownAlt } from "react-icons/fa";

const Revenue = () => {
  const history = useNavigate();
  const [searchParams] = useSearchParams();
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [currency, setCurrency] = React.useState("");
  const [subPlan, setSubcriptionPlan] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [subPlanIndex, setSubPlanIndex] = React.useState(0);
  const [currencyIndex, setCurrencyIndex] = React.useState(0);
  const [dateIndex, setDateIndex] = React.useState(0);
  const [dateFilter, setDateFilter] = React.useState("thisYear");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [sortValue, setSortValue] = React.useState("daysLeft");
  const [order, setOrder] = React.useState("asc");

  const makeAPICall = async ({ queryKey }) => {
    const page = queryKey[1];
    const size = queryKey[2];
    const currency = queryKey[3];
    const plan = queryKey[4];
    const search = queryKey[5];
    const date = queryKey[6];
    const sortBy = queryKey[7];
    const order = queryKey[8];
    const url =
      userAnalyticRequest.USERS_ANALYTIC +
      `?q=subscribed&page=${page}&size=${size}&currency=${currency}&plan=${plan}&search=${search}&date=${date}&sortBy=${sortBy}&order=${order}`;
    const res = await axiosInstance.get(url);
    return res.data;
  };

  const getRevenueSummary = async ({ queryKey }) => {
    const date = queryKey[1];
    const url = userAnalyticRequest.USERS_SUMMARY + `?date=${date}`;
    const res = await axiosInstance.get(url);
    return res.data;
  };

  const { data: userDetails = {}, isLoading } = useQuery(
    [
      QUERY_KEYS.USER_ANALYTICS,
      currentPage,
      pageSize,
      currency,
      subPlan,
      search,
      dateFilter,
      sortValue,
      order,
    ],
    makeAPICall,
  );

  const { data: analyticsSummary = {} } = useQuery(
    [QUERY_KEYS.USER_ANALYTICS, dateFilter],
    getRevenueSummary,
  );

  const { data: subscriptionPlan = {} } = useQueryWrapper(
    QUERY_KEYS.SUBSCRIPTION_PLAN,
    subscriptionPlanRequest.SUBSCRIPTION_PLAN,
  );

  const revenueSummary = useMemo(() => analyticsSummary?.data?.revenue, [analyticsSummary]);

  const subscriptionPlanData = useMemo(() => subscriptionPlan?.data, [subscriptionPlan]);

  useEffect(() => {
    if (startDate.length === 10 && endDate.length === 10) {
      const concatenatedDate = startDate + "," + endDate;
      setDateFilter(concatenatedDate);
      setDateIndex(10);
      handleDateFilter(dateFilter);
    }
  }, [startDate, endDate]);

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", String(newPage));
  };

  const handleSizeChange = (newSize) => {
    searchParams.set("size", String(newSize));
  };

  const handleCurrency = (newCurrency) => {
    searchParams.set("currency", String(newCurrency));
  };

  const handleSubscriptionPlan = (newPlan) => {
    searchParams.set("plan", String(newPlan));
  };

  const handleSearch = (newSearch) => {
    searchParams.set("search", String(newSearch));
  };

  const handleDateFilter = (newDateRange) => {
    searchParams.set("date", String(newDateRange));
  };

  const handleSortChange = (newValue) => {
    setSortValue(newValue);
  };

  const handleOrderChange = (newValue) => {
    setOrder(newValue);
  };

  const { paginationMeta } = userDetails;
  const Users = userDetails.data;

  if (userDetails?.data?.length === 0 && paginationMeta?.totalPages < currentPage) {
    setCurrentPage(0);
    handlePageChange(currentPage);
  }

  const COLUMNS = [
    {
      Header: "Name",
      accessor: "userName",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Subscription Plan",
      Cell: function uploaded({ cell }: any) {
        return <div> {cell.row.original?.subscription?.plan?.name}</div>;
      },
    },
    {
      Header: "Subscription Cost",
      Cell: function uploaded({ cell }: any) {
        return <div> {cell.row.original?.subscription?.transaction?.amount / 100}</div>;
      },
    },
    {
      Header: "Subscription Currency",
      Cell: function uploaded({ cell }: any) {
        return <div> {cell.row.original?.subscription?.transaction?.currency}</div>;
      },
    },
    {
      Header: "Expiration Date",
      Cell: function uploaded({ cell }: any) {
        return <div> {dayjs(cell.row.original?.subscription?.endDate).format("MMM D YYYY")}</div>;
      },
    },
    {
      Header: "Number of Days Left",
      Cell: function uploaded({ cell }: any) {
        return (
          <div>
            {" "}
            {dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
              (1000 * 60 * 60 * 24) <=
            0 ? (
              0
            ) : (
              <div>
                {dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
                  (1000 * 60 * 60 * 24) >
                50 ? (
                  <div>
                    {Math.round(
                      dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
                        (1000 * 60 * 60 * 24 * 30),
                    )}
                    <span> Months</span>
                  </div>
                ) : (
                  <div>
                    {Math.round(
                      dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
                        (1000 * 60 * 60 * 24),
                    )}
                    <span>
                      {Math.round(
                        dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
                          (1000 * 60 * 60 * 24),
                      ) === 1 ? (
                        <span> Day</span>
                      ) : (
                        <span> Days</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const dateFilterData = [
    {
      name: "This Year",
      value: "thisYear",
    },
    {
      name: "This Month",
      value: "thisMonth",
    },
    {
      name: "This Week",
      value: "thisWeek",
    },
    {
      name: "Today",
      value: "today",
    },
    {
      name: "All Time",
      value: "allTime",
    },
  ];

  const colors = ["blue", "green", "orange", "red", "pink", "violet"];
  const bgColors = ["blue.100", "green.100", "orange.100", "red.100", "pink.100", "violet.100"];

  const items = revenueSummary?.map((item, index: number) => {
    return {
      ...item,
      name: index === 0 ? "All Currency" : item?.currency,
      color: colors[index >= colors.length ? 0 : index],
      bgColor: bgColors[index >= colors.length ? 0 : index],
      percentage: item?.percentageOfTotalRevenue,
      amount: index === 0 ? item?.totalAmountCollected : item?.totalAmountCollected / 100,
    };
  });

  items?.sort((a, b) => b.percentage - a.percentage);

  const subscriptionPlanItems = subscriptionPlanData?.map(({ name }) => {
    return { name };
  });

  subscriptionPlanItems?.unshift({ name: "All Plans" });

  return (
    <Container mt="4" maxW="container.xl">
      <Flex justify={"space-between"} mt={"20px"} align="center">
        <Button
          variant="secondary"
          onClick={() => {
            history(-1);
          }}
        >
          Back
        </Button>

        <Box
          display={{
            base: "none",
            lg: "block",
            xl: "block",
          }}
        >
          <InputGroup>
            <InputRightElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputRightElement>
            <Input
              rounded="full"
              type="text"
              placeholder="Search"
              bg="white"
              onChange={(e) => {
                setSearch(e.target.value);
                handleSearch(search);
              }}
            />
          </InputGroup>
        </Box>
      </Flex>

      <Box mt="2rem" display="flex" alignItems="center" gap={5}>
        <Flex border="1px solid #E0E6DD" display="inline-flex" alignItems="stretch" height="44px">
          {dateFilterData.map((item, index: number) => (
            <Button
              key={index}
              _hover={{ border: "1px solid #005aff" }}
              bg={dateIndex === index ? "#1f74cf" : "white"}
              p="20px 20px"
              mb={{
                base: "10px",
                md: "0",
                lg: "0",
              }}
              cursor={dateIndex === index ? "default" : "pointer"}
              pointerEvents={dateIndex === index ? "none" : "auto"}
              color={dateIndex === index ? "white" : "black"}
              onClick={() => {
                setDateFilter(item.value);
                setDateIndex(index);
                setStartDate("");
                setEndDate("");
                handleDateFilter(dateFilter);
              }}
            >
              {item.name}
            </Button>
          ))}
        </Flex>

        <Flex
          width="19rem"
          gap={2}
          cursor={dateIndex === 10 ? "default" : "pointer"}
          pointerEvents={dateIndex === 10 ? "none" : "auto"}
          color={dateIndex === 10 ? "white" : "black"}
        >
          <Input
            height="42.5px"
            placeholder={"Select Start Date"}
            size="sm"
            color={dateIndex === 10 ? "white" : "black"}
            bg={dateIndex === 10 ? "#1f74cf" : "white"}
            borderRadius={dateIndex === 10 ? "md" : ""}
            type={"date"}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
          />
          <Center color="black" pointerEvents="none">
            <ArrowForwardIcon pointerEvents="none" />
          </Center>
          <Input
            height="42.5px"
            placeholder={"Select Start Date"}
            size="sm"
            color={dateIndex === 10 ? "white" : "black"}
            bg={dateIndex === 10 ? "#1f74cf" : "white"}
            borderRadius={dateIndex === 10 ? "md" : ""}
            type={"date"}
            onChange={(e) => {
              setEndDate(e.target.value);
            }}
          />
        </Flex>
      </Box>

      <Skeleton
        isLoaded={!isLoading}
        height="42px"
        width="20rem"
        mt="1rem"
        startColor="#eeeeee"
        endColor="#f6f6f6"
        speed={1.1}
      >
        <Box mt="1rem">
          <Flex border="1px solid #E0E6DD" display="inline-flex" alignItems="stretch" height="44px">
            {subscriptionPlanItems?.map((item, index: number) => (
              <Button
                key={index}
                _hover={{ border: "1px solid #005aff" }}
                bg={subPlanIndex === index ? "#135fa8" : "white"}
                p="20px 20px"
                mb={{
                  base: "10px",
                  md: "0",
                  lg: "0",
                }}
                cursor={subPlanIndex === index ? "default" : "pointer"}
                pointerEvents={subPlanIndex === index ? "none" : "auto"}
                color={subPlanIndex === index ? "white" : "black"}
                onClick={() => {
                  setSubcriptionPlan(index === 0 ? "" : item.name);
                  setSubPlanIndex(index);
                  handleSubscriptionPlan(subPlan);
                }}
              >
                {item.name}
              </Button>
            ))}
          </Flex>
        </Box>
      </Skeleton>

      <Flex width="19rem" gap={2} cursor={"pointer"} color={"black"} mt="1rem">
        <Box textAlign="left">
          <Menu>
            <MenuButton
              mt="1rem"
              mb="2rem"
              as={Button}
              aria-label="Sort scripts"
              leftIcon={<BiFilter />}
              variant="outline"
              fontWeight="normal"
            >
              Sort By:{" "}
              {sortValue === "userName"
                ? "User Name"
                : sortValue === "email"
                ? "Email"
                : sortValue === "startDate"
                ? "Start Date"
                : "Days Left"}{" "}
            </MenuButton>
            <MenuList minWidth="240px" zIndex={14}>
              <MenuOptionGroup
                onChange={handleSortChange}
                defaultValue="userName"
                title="Sort"
                type="radio"
              >
                <MenuItemOption value="userName">User Name</MenuItemOption>
                <MenuItemOption value="email">Email</MenuItemOption>
                <MenuItemOption value="startDate">Start Date</MenuItemOption>
                <MenuItemOption value="daysLeft">Days Left</MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Box>
        <Box textAlign="left">
          <Menu>
            <MenuButton
              mt="1rem"
              mb="2rem"
              as={Button}
              aria-label="Sort scripts"
              leftIcon={order === "asc" ? <FaSortAlphaUpAlt /> : <FaSortAlphaDownAlt />}
              variant="outline"
              fontWeight="normal"
            >
              Order: {order === "asc" ? "Ascending" : "Descending"}{" "}
            </MenuButton>
            <MenuList minWidth="240px" zIndex={14}>
              <MenuOptionGroup
                onChange={handleOrderChange}
                defaultValue="asc"
                title="Order"
                type="radio"
              >
                <MenuItemOption value="asc">Ascending</MenuItemOption>
                <MenuItemOption value="desc">Descending</MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Box>
      </Flex>

      <Grid
        gridTemplateColumns={{
          md: "repeat(2, 1fr)",
          xl: "repeat(4, 1fr)",
        }}
        display={{
          base: "block",
          md: "grid",
          xl: "grid",
        }}
        mt="15px"
        gridGap="24px"
      >
        {items?.map((item, index: number) => {
          return (
            <Skeleton
              key={index}
              isLoaded={!isLoading}
              height="10rem"
              width="100%"
              borderRadius="10px"
              mt="1rem"
              startColor="#eeeeee"
              endColor="#f6f6f6"
              speed={1.1}
            >
              <GridItem
                _hover={{ border: `2px solid ${item.color}` }}
                key={index}
                p="30px 20px"
                mb={{
                  base: "10px",
                  md: "0",
                  lg: "0",
                }}
                bg={item.bgColor}
                borderRadius="10px"
                border={currencyIndex === index ? `4px solid ${item?.color}` : "2px solid #E0E6DD"}
                cursor={currencyIndex === index ? "default" : "pointer"}
                pointerEvents={currencyIndex === index ? "none" : "auto"}
                color="black"
                onClick={() => {
                  setCurrency(index === 0 ? "" : item.currency);
                  setCurrencyIndex(index);
                  handleCurrency(currency);
                }}
              >
                <Box>
                  <Flex justifyContent="space-between" mt="-20px">
                    <Text fontSize="20px" fontWeight="800">
                      {item?.name}
                    </Text>
                  </Flex>

                  <Flex justifyContent="space-between" align="center" mt="20px">
                    <Flex
                      bg={item.color}
                      borderRadius="15px"
                      px="10px"
                      py="5px"
                      alignItems="center"
                      color="white"
                    >
                      <Text fontSize="10px" pr="5px" fontWeight="900">
                        {String(Math.round(Number(item?.percentageOfTotalRevenue))) + "%"}
                      </Text>
                      <GrLineChart size="12px" color="white" />
                    </Flex>

                    <Box>
                      <Heading textAlign="end" fontSize="25px" fontWeight="800">
                        {new Intl.NumberFormat(`en-${item?.currency.slice(0, -1)}`, {
                          style: "currency",
                          currency: item?.currency,
                        }).format(Math.round(Number(item?.amount)))}
                      </Heading>
                    </Box>
                  </Flex>
                </Box>
                <Progress value={item.percentage} size="xs" colorScheme={item.color} mt="20px" />
              </GridItem>
            </Skeleton>
          );
        })}
      </Grid>

      <Box p={"0 10px"} mt="2">
        <AdminDataTable
          ariaLabel="Revenue"
          columns={COLUMNS}
          data={Users}
          loading={isLoading}
          showRowNumbers
          startIndex={pageSize * currentPage + 1}
        />
      </Box>
      <Center>
        <Skeleton
          isLoaded={!isLoading}
          height="40px"
          width="60px"
          mt="1rem"
          startColor="#eeeeee"
          endColor="#f6f6f6"
          speed={1.1}
        >
          {!Users || Users.length === 0 ? (
            ""
          ) : (
            <Paginator
              pageCount={paginationMeta?.totalPages}
              onPageChange={(page) => {
                setCurrentPage(Number(page.selected));
                handlePageChange(currentPage);
              }}
              pageNo={paginationMeta?.currentPage}
              pageSize={pageSize}
              onSizeChange={(selectedPage) => {
                setPageSize(Number(selectedPage));
                handleSizeChange(pageSize);
              }}
            />
          )}
        </Skeleton>
      </Center>
    </Container>
  );
};
export default Revenue;
